import {
    Editor,
    Element as SlateElement,
    Node,
    Path,
    Range,
    Transforms,
} from "slate";
import { ListType } from "./slateTypes";
import {
    emptyList,
    getListItemEntry,
    indentListItem,
    isAtStartOfListItem,
    isCurrentListItemEmpty,
    isInlineChild,
    isInListItem,
    isListElement,
    isListItemElement,
    outdentListItem,
    unwrapCurrentListItem,
    unwrapToParagraph,
    wrapInList,
} from "./SlateEditor.list-model";

const BULLET_MARKERS = ["-", "*"] as const;
const NUMBERED_MARKER = /^\d+[.)]$/;

export type ListKeyDownResult = "handled" | "allow-default" | "unhandled";

interface ListKeyEvent {
    key: string;
    shiftKey: boolean;
}

export const isListActive = (editor: Editor, format: ListType): boolean => {
    const entry = getListItemEntry(editor);
    if (!entry) {
        return false;
    }
    const parent = Node.parent(editor, entry[1]);
    return isListElement(parent) && parent.type === format;
};

export const toggleList = (editor: Editor, format: ListType): void => {
    const isActive = isListActive(editor, format);
    Editor.withoutNormalizing(editor, () => {
        unwrapToParagraph(editor);
        if (!isActive) {
            wrapInList(editor, format);
        }
    });
};

export const isChecklistActive = (editor: Editor): boolean => {
    const entry = getListItemEntry(editor);
    if (!entry) {
        return false;
    }
    const [item] = entry;
    return item.checked === true || item.checked === false;
};

export const toggleChecklist = (editor: Editor): void => {
    const isActive = isChecklistActive(editor);
    Editor.withoutNormalizing(editor, () => {
        unwrapToParagraph(editor);
        if (!isActive) {
            wrapInList(editor, "bulleted-list", { checked: false });
        }
    });
};

const checklistCheckedForMarker = (marker: string): boolean | undefined => {
    if (marker === "[]" || marker === "[ ]") {
        return false;
    }
    if (marker === "[x]" || marker === "[X]") {
        return true;
    }
    return undefined;
};

const listShortcutForMarker = (
    marker: string,
): { format: ListType; checked?: boolean } | null => {
    const isChecked = checklistCheckedForMarker(marker);
    if (isChecked !== undefined) {
        return { format: "bulleted-list", checked: isChecked };
    }
    if (BULLET_MARKERS.some((item) => item === marker)) {
        return { format: "bulleted-list" };
    }
    if (NUMBERED_MARKER.test(marker)) {
        return { format: "numbered-list" };
    }
    return null;
};

const tryConvertMarkdownListShortcut = (editor: Editor): boolean => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return false;
    }
    if (isInListItem(editor)) {
        return false;
    }

    const blockEntry = Editor.above(editor, {
        match: (node) =>
            SlateElement.isElement(node) && node.type === "paragraph",
    });
    if (!blockEntry) {
        return false;
    }

    const [, path] = blockEntry;
    const markerRange = {
        anchor: Editor.start(editor, path),
        focus: selection.anchor,
    };
    const shortcut = listShortcutForMarker(Editor.string(editor, markerRange));
    if (!shortcut) {
        return false;
    }

    Editor.withoutNormalizing(editor, () => {
        Transforms.select(editor, markerRange);
        Transforms.delete(editor);
        wrapInList(
            editor,
            shortcut.format,
            shortcut.checked === undefined
                ? undefined
                : { checked: shortcut.checked },
        );
    });
    return true;
};

export const handleListKeyDown = (
    editor: Editor,
    event: ListKeyEvent,
): ListKeyDownResult => {
    if (event.key === "Tab" && isInListItem(editor)) {
        if (event.shiftKey) {
            outdentListItem(editor);
        } else {
            indentListItem(editor);
        }
        return "handled";
    }

    if (event.key === "Enter" && !event.shiftKey && isInListItem(editor)) {
        return "allow-default";
    }

    if (event.key === "Backspace" && isAtStartOfListItem(editor)) {
        unwrapCurrentListItem(editor);
        return "handled";
    }

    return "unhandled";
};

export const withLists = (editor: Editor): Editor => {
    const { insertText, insertBreak, normalizeNode } = editor;

    editor.insertText = (text) => {
        if (text === " " && tryConvertMarkdownListShortcut(editor)) {
            return;
        }
        insertText(text);
    };

    editor.insertBreak = () => {
        if (!isInListItem(editor)) {
            insertBreak();
            return;
        }
        if (isCurrentListItemEmpty(editor)) {
            unwrapCurrentListItem(editor);
            return;
        }
        Transforms.splitNodes(editor, {
            match: isListItemElement,
            always: true,
        });
    };

    editor.normalizeNode = (entry) => {
        const [node, path] = entry;

        if (isListElement(node)) {
            if (node.children.length === 0) {
                Transforms.removeNodes(editor, { at: path });
                return;
            }
            if (path[path.length - 1] > 0) {
                const previous = Node.get(editor, Path.previous(path));
                if (isListElement(previous) && previous.type === node.type) {
                    Transforms.mergeNodes(editor, { at: path });
                    return;
                }
            }
        }

        if (isListItemElement(node)) {
            const parent = Node.parent(editor, path);
            if (isListItemElement(parent) || !isListElement(parent)) {
                Transforms.wrapNodes(editor, emptyList("bulleted-list"), {
                    at: path,
                });
                return;
            }
            if (node.children.length === 0) {
                Transforms.insertNodes(
                    editor,
                    { type: "paragraph", children: [{ text: "" }] },
                    { at: [...path, 0] },
                );
                return;
            }
            const inlineIndex = node.children.findIndex((child) =>
                isInlineChild(child),
            );
            if (inlineIndex >= 0) {
                Transforms.wrapNodes(
                    editor,
                    { type: "paragraph", children: [] },
                    { at: [...path, inlineIndex] },
                );
                return;
            }
        }

        if (
            SlateElement.isElement(node) &&
            node.type === "paragraph" &&
            isListElement(Node.parent(editor, path))
        ) {
            Transforms.wrapNodes(
                editor,
                { type: "list-item", children: [] },
                { at: path },
            );
            return;
        }

        normalizeNode(entry);
    };

    return editor;
};
